import type { AlertReason } from "@beacon/core/chat"
import type { PushPayload, PushPreview, PushSource } from "@beacon/core/chat-push"
import { messageHref } from "./message-place.ts"

/*
 * What a Chat push shows, decided in the service worker (`sw.ts`) from what the push carries: ids
 * and why it alerts (`PushPayload`), never anything a Member wrote. By default it says only that
 * something in Chat is for the Member, and why, so a locked screen shows nothing of a conversation.
 * Where its Member turned previews on, the worker asks the web API for one as it shows the push
 * (`PushPreview`), and shows it when it comes: who said it, where, and the start of what they said.
 * Opening it opens the message where it was said, in the web app, which reads it through the
 * audience check as it is then: a message gone, or a conversation the Member can no longer read,
 * opens on Chat's own word that it is gone. Each push of one message has its one notification
 * (`tag`), so a retry, or a second push of it, never shows twice. A notice in the Beacon inbox
 * (`work-notice`) says by default only that one came, its preview what it says and what it is
 * about, and opening it opens the inbox.
 */

/** What a notification says when it says nothing anyone wrote, by why it alerts. */
const said: Record<AlertReason, string> = {
  mention: "You were mentioned in Chat",
  "group-mention": "A Group you're in was mentioned in Chat",
  dm: "New direct message",
  "thread-reply": "New reply in a thread you follow",
  "channel-mention": "@channel in a channel you joined",
  "here-mention": "@here in a channel you joined",
  "channel-message": "New message in a channel you joined",
  "work-notice": "New in your Beacon inbox",
}

const id = /^[0-9a-f-]{36}$/i

/** A push's payload, trusted only as far as it holds what Chat push sends; none otherwise. */
export function parsePush(text: string | undefined): PushPayload | null {
  try {
    const parsed = JSON.parse(text ?? "") as Partial<PushPayload> | null
    if (parsed?.kind === "work-notice") {
      const notice = parsed as Partial<Extract<PushPayload, { kind: "work-notice" }>>
      return typeof notice.entryId === "string" &&
        id.test(notice.entryId) &&
        notice.reason === "work-notice" &&
        typeof notice.previews === "boolean"
        ? (notice as PushPayload)
        : null
    }
    const value = parsed as Partial<Extract<PushPayload, { kind: "chat-message" }>>
    if (
      value.kind !== "chat-message" ||
      typeof value.conversationId !== "string" ||
      !id.test(value.conversationId) ||
      typeof value.messageId !== "string" ||
      !id.test(value.messageId) ||
      (value.threadRootId !== null &&
        (typeof value.threadRootId !== "string" || !id.test(value.threadRootId))) ||
      typeof value.reason !== "string" ||
      !Object.hasOwn(said, value.reason) ||
      typeof value.previews !== "boolean"
    )
      return null
    return value as PushPayload
  } catch {
    return null
  }
}

/** What a push is of, as the web API names it to give its preview. */
export const pushSourceOf = (payload: PushPayload): PushSource =>
  payload.kind === "work-notice"
    ? { kind: "inbox-entry", id: payload.entryId }
    : { kind: "message", id: payload.messageId }

/** The web API's answer for a preview, when it is one: a title and body of text, held short. */
export function previewFrom(answer: unknown): PushPreview | null {
  const preview = (answer as { preview?: unknown } | null)?.preview as Partial<PushPreview> | null
  if (!preview || typeof preview.title !== "string" || typeof preview.body !== "string") return null
  return { title: preview.title.slice(0, 200), body: preview.body.slice(0, 300) }
}

/**
 * What the service worker tells a tab already open when a notification is opened, with the path
 * to open (`{ type, path }`), so the tab opens it in place rather than another opening.
 */
export const pushOpenMessage = "beacon:chat-push-open"

/** What opening a notification opens: the message's place, or Chat for a push it couldn't read. */
export type NotificationData = { path: string }

/** The notification a push shows: its title, and the options `showNotification` takes. */
export function notificationOf(payload: PushPayload | null, preview: PushPreview | null) {
  if (!payload)
    return {
      title: "Beacon",
      options: {
        body: "Something new in Chat",
        tag: "chat",
        icon: "/pwa-192x192.png",
        data: { path: "/chat" } satisfies NotificationData,
      },
    }
  if (payload.kind === "work-notice")
    return {
      title: preview?.title ?? "Beacon",
      options: {
        body: preview?.body ?? said[payload.reason],
        tag: `inbox:${payload.entryId}`,
        icon: "/pwa-192x192.png",
        data: { path: "/chat/inbox" } satisfies NotificationData,
      },
    }
  return {
    title: preview?.title ?? "Beacon",
    options: {
      body: preview?.body ?? said[payload.reason],
      tag: `chat:${payload.messageId}`,
      icon: "/pwa-192x192.png",
      data: {
        path: messageHref({
          conversationId: payload.conversationId,
          id: payload.messageId,
          threadRootId: payload.threadRootId,
        }),
      } satisfies NotificationData,
    },
  }
}

/** Where opening a notification goes: its path when it is Chat's, Chat itself otherwise. */
export function pathOf(data: unknown): string {
  const path = (data as Partial<NotificationData> | null)?.path
  return typeof path === "string" && /^\/chat(?:[/?]|$)/.test(path) && !path.startsWith("//")
    ? path
    : "/chat"
}
